import { useTextLanguage, useDisabled } from '../../../hooks/useShared';
import { useFormContext } from 'react-hook-form';
import { useEdit } from '../../../hooks/useEdit';
import { useCheckboxRadio } from '../../../hooks/useSelect';
import { useTranslation } from 'react-i18next';
import { Col, Form } from 'react-bootstrap';
import { StyledGroup } from '../../shared/Input/styled';
import { Error } from '../../shared/simple/Error';
import { Label } from '../../shared/simple/Label';
import { TooltipIcon } from '../../shared/simple/TooltipIcon';
import { StyledCheck } from '../styled';
import useDateMonthly from '../../../hooks/useDateMonthly';
import InternalSelector from './InternalSelector';

/**
 * Component radio select
 * @component
 * @param {Object} field
 * @returns
 */
const DateMonthly = ({ field }) => {
  const uniqueName = `${field.unique_name}__id__${field.id}`;
  const { description, languageText } = useTextLanguage();
  const { register, formState: { errors }, setValue } = useFormContext();
  const { colSize } = useEdit(field);
  const { onClick } = useCheckboxRadio(field);
  const { 
    fullOptions, summedOptions, week_days, calculateFirstVal,
    calculateSecondVal, internalSetValue, firstCheckName,
    secondCheckName, firstLabelClassNames, secondLabelClassNames, 
    functionGetValues, selectNames
  } = useDateMonthly(field, setValue)
  const { isDisabled } = useDisabled(field);
  const { t } = useTranslation();
  // Names for InternalSelectors
  const [
    firstSelectName,
    secondSelectName,
    thirdSelectName,
    fourthSelectName,
    fifthSelectName,
    sixthSelectName,
    seventhSelectName,
    eighthSelectName,
    ninthSelectName    
  ] = selectNames;

  return (
    <>
      {/* PAYMENT TYPE MONTHLY */}
      {field && (functionGetValues?.type === 'monthly') ? (
        <>
          {
            (field && field.col_size?.includes('col-before')) && (
              <Col xs={12} key={`col_before_${field.id}`}></Col>
            )
          }
          {
            <Col
              className={ colSize }
              data-module={ `module-${field?.category?.field_category}` }
              data-name={ uniqueName }
              data-type='radio'
              id={ `group_field_${field.id}` }
              key={ field.id }
              xs={12}
            >
              <StyledGroup
                aria-labelledby="checkbox-group"
                className="mb-3"
                role="group"
                data-unique-name={ uniqueName }
              >
                <Label
                  className="d-block"
                  htmlFor={ uniqueName }
                  xs={12}
                >
                  { field[description] }{ (field.required) && (
                    <strong className="text-danger">&nbsp;*</strong>)}
                  { (field?.[`tooltip${languageText}`]) && (
                    <TooltipIcon tooltipText={ field[`tooltip${languageText}`] } />)}
                </Label>
                
                {/* First month-option */}
                <StyledGroup
                  className="mb-0"
                  key={ `group_check_1` }
                >
                  <Form.Check className='d-flex align-items-center'>

                    <StyledCheck
                      { ...register(uniqueName) }
                      data-category={`category-${field?.category?.field_category}` }
                      data-unique-name={firstCheckName}
                      name={ uniqueName }
                      onClick={ onClick }
                      type="radio"
                      value={calculateFirstVal()}
                      disabled={ isDisabled }
                      id={ field.id }
                      data-action={ field.action }
                      data-condition={ field.parent_condition }
                      data-parent={ field.parent }
                    />
                    <Form.Check.Label>
                      <div className={firstLabelClassNames}>
                        <span>
                          {t('dateMonthly.the')} 
                        </span>
                        <InternalSelector 
                          options={fullOptions}
                          width='90'
                          text='ordinal'
                          parentCheckName={firstCheckName}
                          uniqueName={uniqueName}
                          setValue={internalSetValue}
                          checkNumber={1}
                          selectorName={firstSelectName}
                        />
                        <span>
                          {t('dateMonthly.ofEach')} 
                        </span>
                      </div>
                    </Form.Check.Label>

                  </Form.Check>
                </StyledGroup>
                {/* Second month-option */}
                <StyledGroup
                  className="mb-0"
                  key={ `group_check_2` }
                >
                  <Form.Check className='d-flex align-items-center'>
                    <StyledCheck
                      { ...register(uniqueName) }
                      data-category={`category-${field?.category?.field_category}` }
                      data-unique-name={secondCheckName}
                      name={ uniqueName }
                      onClick={ onClick }
                      type="radio"
                      value={calculateSecondVal()}
                      disabled={ isDisabled }
                      id={ field.id }
                      data-action={ field.action }
                      data-condition={ field.parent_condition }
                      data-parent={ field.parent }
                    />
                    <Form.Check.Label>
                      <div className={secondLabelClassNames}>
                        {t('dateMonthly.the')} 
                        <InternalSelector 
                          width='140'
                          options={summedOptions}
                          text={'description'}
                          uniqueName={uniqueName}
                          parentCheckName={secondCheckName}
                          setValue={internalSetValue}
                          checkNumber={2}
                          selectorName={secondSelectName}
                        />
                        <InternalSelector 
                          width='200'
                          options={week_days}
                          text='description'
                          parentCheckName={secondCheckName}
                          uniqueName={uniqueName}
                          setValue={internalSetValue}
                          checkNumber={3}
                          selectorName={thirdSelectName}
                        />
                      </div>
                    </Form.Check.Label>
                  </Form.Check>
                </StyledGroup>

                {errors?.[uniqueName]?.message && (
                  <Error message={ errors[uniqueName].message } />
                )}
              </StyledGroup>
            </Col>
          }
        </>
      ): null}

      {/* PAYMENT TYPE TWICE PER MONTH */}
      {field && (functionGetValues?.type === 'bimonthly') ? (
        <>
          {(field && field.col_size?.includes('col-before')) && (
            <Col xs={12} key={`col_before_${field.id}`}></Col>
          )}
          <Col
              className={ colSize }
              data-module={ `module-${field?.category?.field_category}` }
              data-name={ uniqueName }
              data-type='radio'
              id={ `group_field_${field.id}` }
              key={ field.id }
              xs={12}
            >
              <StyledGroup
                aria-labelledby="checkbox-group"
                className="mb-3"
                role="group"
                data-unique-name={ uniqueName }
              >
                <Label
                  className="d-block"
                  htmlFor={ uniqueName }
                  xs={12}
                >
                  { field[description] }{ (field.required) && (
                    <strong className="text-danger">&nbsp;*</strong>)}
                  { (field?.[`tooltip${languageText}`]) && (
                    <TooltipIcon tooltipText={ field[`tooltip${languageText}`] } />)}
                </Label>

                {/* First month-option */}
                <StyledGroup
                  className="mb-0"
                  key={ `group_check_1` }
                >
                  <Form.Check className='d-flex align-items-center'>

                    <StyledCheck
                      { ...register(uniqueName) }
                      data-category={`category-${field?.category?.field_category}` }
                      data-unique-name={firstCheckName}
                      name={ uniqueName }
                      onClick={ onClick }
                      type="radio"
                      value={calculateFirstVal()}
                      disabled={ isDisabled }
                      id={ field.id }
                      data-action={ field.action }
                      data-condition={ field.parent_condition }
                      data-parent={ field.parent }
                    />
                    <Form.Check.Label>
                      <div className={firstLabelClassNames}>
                        <span>
                          {t('dateMonthly.the')} 
                        </span>
                        <InternalSelector 
                          options={fullOptions}
                          width='90'
                          text='ordinal'
                          parentCheckName={firstCheckName}
                          uniqueName={uniqueName}
                          setValue={internalSetValue}
                          checkNumber={4}
                          selectorName={fourthSelectName}
                        />
                        <span>
                          {t('dateMonthly.and')}
                        </span>
                        <InternalSelector 
                          options={fullOptions}
                          width='90'
                          text='ordinal'
                          parentCheckName={firstCheckName}
                          uniqueName={uniqueName}
                          setValue={internalSetValue}
                          checkNumber={5}
                          selectorName={fifthSelectName}
                        />
                        <span>
                          {t('dateMonthly.ofEach')} 
                        </span>
                      </div>
                    </Form.Check.Label>

                  </Form.Check>
                </StyledGroup>



                {/* Second month-option */}
                <StyledGroup
                  className="mb-0"
                  key={ `group_check_2` }
                >
                  <Form.Check className='d-flex align-items-center'>
                    <StyledCheck
                      { ...register(uniqueName) }
                      data-category={`category-${field?.category?.field_category}` }
                      data-unique-name={secondCheckName}
                      name={ uniqueName }
                      onClick={ onClick }
                      type="radio"
                      value={calculateSecondVal()}
                      disabled={ isDisabled }
                      id={ field.id }
                      data-action={ field.action }
                      data-condition={ field.parent_condition }
                      data-parent={ field.parent }
                    />
                    <Form.Check.Label>
                      <div className={secondLabelClassNames}>
                        {t('dateMonthly.the')} 
                        <InternalSelector 
                          width='110'
                          options={summedOptions}
                          text={'description'}
                          uniqueName={uniqueName}
                          parentCheckName={secondCheckName}
                          setValue={internalSetValue}
                          checkNumber={6}
                          selectorName={sixthSelectName}
                        />
                        <InternalSelector 
                          width='110'
                          options={week_days}
                          text='description'
                          parentCheckName={secondCheckName}
                          uniqueName={uniqueName}
                          setValue={internalSetValue}
                          checkNumber={7}
                          selectorName={seventhSelectName}
                        />
                        <span>
                          {t('dateMonthly.and')}
                        </span>
                        <InternalSelector 
                          width='110'
                          options={summedOptions}
                          text={'description'}
                          uniqueName={uniqueName}
                          parentCheckName={secondCheckName}
                          setValue={internalSetValue}
                          checkNumber={8}
                          selectorName={eighthSelectName}
                        />
                        <InternalSelector 
                          width='110'
                          options={week_days}
                          text='description'
                          parentCheckName={secondCheckName}
                          uniqueName={uniqueName}
                          setValue={internalSetValue}
                          checkNumber={9}
                          selectorName={ninthSelectName}
                        />                  
                      </div>
                    </Form.Check.Label>
                  </Form.Check>
                </StyledGroup>

                {errors?.[uniqueName]?.message && (
                  <Error message={ errors[uniqueName].message } />
                )}

              </StyledGroup>
          </Col>
        </>
        
      ) : null}
    </>
  )
}

export default DateMonthly